import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Upload, ReceiptText } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { ArInvoiceDrawer } from '../../components/ArInvoiceDrawer';
import { ArInvoiceCreateDrawer } from '../../components/ArInvoiceCreateDrawer';
import { useArInvoices, useUploadArInvoice, type ArInvoice } from '../../hooks/useArInvoices';
import { QueryError } from '../../components/ui/QueryError';
import { EmptyState } from '../../components/ui/EmptyState';
import { formatAmount, formatDate } from '../../lib/format';
import { useCan } from '../../hooks/useCan';

const STATUS_TONE: Record<ArInvoice['status'], 'neutral' | 'accent' | 'success' | 'warn' | 'muted'> = {
  draft: 'neutral',
  sent: 'accent',
  partial: 'accent',
  paid: 'success',
  overdue: 'warn',
  void: 'muted',
};

export function InvoicesArPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const { data: invoices, isLoading, isError, refetch } = useArInvoices(wsId);
  const upload = useUploadArInvoice(wsId);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  // Deep-link support: a collections row or signal can link to
  // `…/ar-invoices?open=<id>` to open the receivable drawer directly.
  useEffect(() => {
    const open = searchParams.get('open');
    if (open && open !== drawerId) setDrawerId(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const openDrawer = (id: string | null) => {
    setDrawerId(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('open', id);
        else next.delete('open');
        return next;
      },
      { replace: true },
    );
  };

  const handleFile = async (file: File) => {
    setUploadErr(null);
    try {
      const created = await upload.mutateAsync(file);
      if (created?.id) openDrawer(created.id);
    } catch (err: any) {
      setUploadErr(err?.response?.data?.message ?? err?.message ?? 'Upload failed');
    }
  };

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div>
            <h1 className="display text-2xl font-semibold">{t('invoicesAr.title')}</h1>
            <p className="text-sm text-ink-500">{t('invoicesAr.subtitle')}</p>
          </div>
          {canMutate && (
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 cursor-pointer text-sm font-medium">
                <Upload size={14} />
                {t('inbox.uploadInvoice')}
                <input
                  type="file"
                  hidden
                  accept="application/pdf,image/*,text/html,text/plain,.pdf,.html,.htm,.txt"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
              </label>
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium"
              >
                <Plus size={14} />
                {t('invoicesAr.createInvoice')}
              </button>
            </div>
          )}
        </div>

        {uploadErr && <div className="mt-4 text-sm text-warn-600">{uploadErr}</div>}

        <Card className="mt-6">
          {isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : isError ? (
            <QueryError onRetry={refetch} />
          ) : !invoices || invoices.length === 0 ? (
            <EmptyState
              icon={ReceiptText}
              title={t('emptyState.invoicesAr.title')}
              description={t('emptyState.invoicesAr.body')}
              primaryLabel={t('invoicesAr.createInvoice')}
              primaryIcon={Plus}
              onPrimary={() => setCreateOpen(true)}
              offerSampleData
              wsId={wsId}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-ink-500 border-b border-ink-200 dark:border-ink-800">
                    <th className="px-5 py-3 font-medium">#</th>
                    <th className="px-5 py-3 font-medium">{t('common.issue')}</th>
                    <th className="px-5 py-3 font-medium">{t('common.due')}</th>
                    <th className="px-5 py-3 font-medium">{t('common.status')}</th>
                    <th className="px-5 py-3 font-medium">{t('invoicesAr.peppolColumn')}</th>
                    <th className="px-5 py-3 font-medium text-right">{t('common.amount')}</th>
                    <th className="px-5 py-3 font-medium text-right">{t('invoicesAr.paidColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr
                      key={inv.id}
                      onClick={() => openDrawer(inv.id)}
                      className="border-b border-ink-200 dark:border-ink-800 hover:bg-ink-50 dark:hover:bg-ink-900 cursor-pointer"
                    >
                      <td className="px-5 py-3">{inv.invoice_number ?? '—'}</td>
                      <td className="px-5 py-3">{formatDate(inv.issue_date, i18n.language)}</td>
                      <td className="px-5 py-3">{formatDate(inv.due_date, i18n.language)}</td>
                      <td className="px-5 py-3">
                        <Pill tone={STATUS_TONE[inv.status]}>{t(`invoicesAr.status.${inv.status}`)}</Pill>
                      </td>
                      <td className="px-5 py-3 text-xs text-ink-500">
                        {inv.peppol_sent_at ? formatDate(inv.peppol_sent_at, i18n.language) : '—'}
                      </td>
                      <td className="px-5 py-3 text-right num">
                        {formatAmount(inv.total_cents, inv.currency, i18n.language)}
                      </td>
                      <td className="px-5 py-3 text-right num text-ink-500">
                        {formatAmount(inv.paid_cents, inv.currency, i18n.language)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {wsId && (
        <>
          <ArInvoiceDrawer
            wsId={wsId}
            invoiceId={drawerId}
            open={!!drawerId}
            onOpenChange={(o) => !o && openDrawer(null)}
          />
          <ArInvoiceCreateDrawer
            wsId={wsId}
            open={createOpen}
            onOpenChange={setCreateOpen}
            onCreated={(id) => openDrawer(id)}
          />
        </>
      )}
    </AppShell>
  );
}
